import React, { useState, useEffect, useContext } from 'react'

import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import axios from 'axios'
import Cookie from 'js-cookie'

import { COOKIE_ACCESS_TOKEN_NAME } from 'constants/cookiesConsts'
import { API_SHIPMENT } from 'constants/urls'
import useAuthContext from 'contextApi/AuthContext'
import { encryptAES256CBC } from 'utils/encryptData'
import useStyles from './modalManualGuide.styles'

const STEP = {
  FORM: 'form',
  CONFIRM: 'confirm',
  SUCCESS: 'success',
  ERROR: 'error',
  INVALID: 'invalid',
}

const ERROR_TITLE_INVALID_GUIDE = 'shipp_fail'
const OBSERVATIONS_MAX_LENGTH = 255
const SUCCESS_AUTO_CLOSE_MS = 2000

const httpClient = axios.create()

const ModalManualGuide = ({ open, onClose, product, orderId, productCount, onSuccess }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const { clientIp } = useContext(useAuthContext)
  const [step, setStep] = useState(STEP.FORM)
  const [guideNumber, setGuideNumber] = useState('')
  const [observations, setObservations] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])

  useEffect(() => {
    if (step === STEP.SUCCESS) {
      const timer = setTimeout(() => {
        onSuccess()
        onClose()
      }, SUCCESS_AUTO_CLOSE_MS)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [step])

  const resetAndClose = () => {
    setStep(STEP.FORM)
    setGuideNumber('')
    setObservations('')
    setErrorMessages([])
    onClose()
  }

  const handleConfirm = async () => {
    setIsLoading(true)
    try {
      const token = Cookie.get(COOKIE_ACCESS_TOKEN_NAME)
      const response = await httpClient.post(
        `${API_SHIPMENT}/GuideAssignment?oldGuideNumber=${product?.newGuideNumber || product?.guideNumber}&newGuideNumber=${guideNumber}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Client-Token': encryptAES256CBC(clientIp),
          },
        }
      )
      setStep(response.status === 200 ? STEP.SUCCESS : STEP.ERROR)
    } catch (error) {
      const data = error?.response?.data
      const messages = data?.errors || []
      setErrorMessages(messages)
      setStep(data?.title === ERROR_TITLE_INVALID_GUIDE ? STEP.INVALID : STEP.ERROR)
    } finally {
      setIsLoading(false)
    }
  }

  const renderForm = () => (
    <>
      <DialogTitle disableTypography className={classes.titleRow}>
        <Typography variant="h6" className={classes.title}>
          Agregar guía manual
        </Typography>
        <IconButton size="small" onClick={resetAndClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContentVisibleOverflow}>
        <Typography variant="body2" className={classes.subtitle}>
          Ingresa la nueva guía para continuar.
          <br />
          Hay <strong>{productCount} {productCount === 1 ? 'producto' : 'productos'}</strong> en esta guía.
        </Typography>

        <Typography variant="body2" className={classes.fieldLabel}>
          Transportadora
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          value={product?.transportCompany || ''}
          InputProps={{ readOnly: true }}
          className={classes.readOnlyField}
          size="small"
        />

        <Typography variant="body2" className={classes.fieldLabel}>
          Número nueva guía
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          value={guideNumber}
          onChange={(e) => setGuideNumber(e.target.value)}
          className={classes.field}
          size="small"
        />

        <Typography variant="body2" className={classes.fieldLabel}>
          Observaciones{' '}
          <span className={classes.optionalLabel}>(opcional)</span>
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          value={observations}
          onChange={(e) => {
            if (e.target.value.length <= OBSERVATIONS_MAX_LENGTH) setObservations(e.target.value)
          }}
          helperText={`${observations.length}/${OBSERVATIONS_MAX_LENGTH}`}
          FormHelperTextProps={{ className: classes.helperText }}
          className={classes.multilineField}
          InputProps={{ className: classes.multilineInput }}
          inputProps={{
            maxLength: OBSERVATIONS_MAX_LENGTH,
            className: classes.multilineTextarea,
          }}
        />

        <Grid container justify="flex-end" spacing={2} className={classes.actionsSpacing}>
          <Grid item>
            <Button variant="outlined" onClick={resetAndClose} className={classes.btnOutlined}>
              Cancelar
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              disabled={!guideNumber.trim()}
              onClick={() => setStep(STEP.CONFIRM)}
              className={classes.btnContained}
            >
              Continuar
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </>
  )

  const renderConfirm = () => (
    <>
      <DialogTitle disableTypography className={classes.titleRow}>
        <Typography variant="h6" className={classes.title}>
          Confirmar guía manual
        </Typography>
        <IconButton size="small" onClick={resetAndClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" className={classes.subtitle}>
          Ingresa la nueva guía para continuar.
          <br />
          Hay <strong>{productCount} {productCount === 1 ? 'producto' : 'productos'}</strong> en esta guía.
        </Typography>

        <div className={classes.summaryBox}>
          <Grid container>
            <Grid item xs={6}>
              <Typography variant="body2" className={classes.summaryLabel}>
                Pedido:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" align="right" className={classes.summaryValue}>
                #{orderId}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" className={classes.summaryLabel}>
                Nueva guía:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" align="right" className={classes.summaryValue}>
                {guideNumber}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" className={classes.summaryLabel}>
                Transportadora:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" align="right" className={classes.summaryValue}>
                {product?.transportCompany}
              </Typography>
            </Grid>
          </Grid>
        </div>

        <div className={classes.infoBox}>
          <ErrorOutlineIcon className={classes.infoIcon} />
          <Typography variant="body2" className={classes.infoText}>
            Al registrar la nueva guía, el tiempo de envío se aumenta 2 día más y se evita la
            cancelación automática de este pedido al Seller.
          </Typography>
        </div>

        <Grid container justify="flex-end" spacing={2} className={classes.actionsSpacing}>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => setStep(STEP.FORM)}
              className={classes.btnOutlined}
            >
              Volver
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={isLoading}
              className={`${classes.btnContained} ${classes.btnContainedWide}`}
            >
              {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Confirmar y agregar'}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </>
  )

  const renderSuccess = () => (
    <DialogContent>
      <Grid container direction="column" alignItems="center" className={classes.successContent}>
        <div className={classes.successIcon}>
          <CheckIcon className={classes.statusIconSymbol} />
        </div>
        <Typography variant="h6" className={classes.title}>
          Nueva guía agregada
        </Typography>
      </Grid>
    </DialogContent>
  )

  const renderError = () => (
    <>
      <DialogTitle disableTypography className={classes.closeOnlyTitleRow}>
        <IconButton size="small" onClick={resetAndClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContentVisibleOverflow}>
        <Grid container direction="column" alignItems="center" className={classes.statusContent}>
          <div className={classes.errorIcon}>
            <CloseIcon className={classes.statusIconSymbol} />
          </div>
          <Typography variant="h6" className={`${classes.title} ${classes.titleWithSpacing}`}>
            Ha ocurrido un error
          </Typography>
          {errorMessages.length > 0 ? (
            errorMessages.map((msg, idx) => (
              <Typography
                key={idx}
                variant="body2"
                align="center"
                className={classes.bodySpacing}
              >
                {msg}
              </Typography>
            ))
          ) : (
            <Typography variant="body2" className={classes.bodySpacing}>
              Vuelve a intentarlo más tarde
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={resetAndClose}
            className={classes.btnContained}
          >
            Cerrar
          </Button>
        </Grid>
      </DialogContent>
    </>
  )

  const renderInvalid = () => (
    <>
      <DialogTitle disableTypography className={classes.closeOnlyTitleRow}>
        <IconButton size="small" onClick={resetAndClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container direction="column" alignItems="center" className={classes.statusContent}>
          <div className={classes.invalidIcon}>
            <ErrorOutlineIcon className={classes.statusIconSymbol} />
          </div>
          <Typography variant="h6" className={`${classes.title} ${classes.titleWithSpacing}`}>
            Guía no válida
          </Typography>
          {errorMessages.map((msg, idx) => (
            <Typography
              key={idx}
              variant="body2"
              align="center"
              className={classes.bodySpacing}
            >
              {msg}
            </Typography>
          ))}
          <Button
            variant="contained"
            color="primary"
            onClick={resetAndClose}
            className={classes.btnContained}
          >
            Cerrar
          </Button>
        </Grid>
      </DialogContent>
    </>
  )

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      disableBackdropClick={step !== STEP.SUCCESS}
    >
      {step === STEP.FORM && renderForm()}
      {step === STEP.CONFIRM && renderConfirm()}
      {step === STEP.SUCCESS && renderSuccess()}
      {step === STEP.ERROR && renderError()}
      {step === STEP.INVALID && renderInvalid()}
    </Dialog>
  )
}

export default ModalManualGuide
